import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button, Form, Modal, Row, Col, Badge, InputGroup, Spinner, Alert } from 'react-bootstrap';
import { FaEdit, FaTrash, FaSearch, FaPlus, FaUsers, FaArrowLeft, FaCheck, FaTimes, FaEye, FaEyeSlash, FaUsersCog, FaSave, FaUpload } from 'react-icons/fa';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { validateOrganizationToken } from '../../../utils/urlSecurity';
import api from '../../../api/apiClient';
import { toast } from 'react-toastify';
import GroupAssignBoard from '../../components/groups/GroupAssignBoard';
import OrgBulkUploadUsersModal from './OrgBulkUploadUsersModal';

const OrgUsers = () => {
  const navigate = useNavigate();
  const { orgId: secureToken } = useParams();
  const location = useLocation();
  const organizationName = location.state?.organizationName || 'Organization';
  
  const [orgId, setOrgId] = useState(null);
  const [tokenValid, setTokenValid] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [twofaLoadingUserId, setTwofaLoadingUserId] = useState(null);
  const [groups, setGroups] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignGroupId, setAssignGroupId] = useState('');
  const [assignAssigned, setAssignAssigned] = useState([]);
  const [assignAvailable, setAssignAvailable] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignSaving, setAssignSaving] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    role: 1,
    status: 1,
    group_ids: []
  });
  const [formErrors, setFormErrors] = useState({});
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);

  // Validate token and extract orgId
  const validateToken = () => {
    const validation = validateOrganizationToken(secureToken);
    
    if (!validation.valid) {
      if (validation.expired) {
        toast.error('Navigation token has expired. Please navigate from the organizations page again.');
      } else {
        toast.error('Invalid navigation token. Please navigate from the organizations page.');
      }
      navigate('/admin/organizations');
      return false;
    }
    
    setOrgId(parseInt(validation.orgId));
    setTokenValid(true);
    return true;
  };

  // Fetch organization users from API
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.organizations.getUsers(orgId, true);
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching organization users:', error);
      toast.error('Failed to load organization users: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (validateToken()) {
    }
  }, [secureToken]);

  const fetchGroups = async () => {
    try {
      const response = await api.groups.list();
      setGroups(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  useEffect(() => {
    if (orgId && tokenValid) {
      fetchUsers();
      fetchGroups();
    }
  }, [orgId, tokenValid]);

  const orgGroups = useMemo(
    () => groups.filter((g) => g.organization_id && parseInt(g.organization_id) === parseInt(orgId)),
    [groups, orgId]
  );

  const handleGroupToggle = (groupId) => {
    const id = groupId.toString();
    setFormData((prev) => {
      const exists = prev.group_ids.includes(id);
      return {
        ...prev,
        group_ids: exists
          ? prev.group_ids.filter((value) => value !== id)
          : [...prev.group_ids, id]
      };
    });
  };

  // group_ids can come back as an array or a JSON string depending on the DB driver
  const parseGroupIds = (value) => {
    let raw = value;
    if (typeof raw === 'string') {
      try {
        raw = JSON.parse(raw);
      } catch {
        return [];
      }
    }
    if (!Array.isArray(raw)) return [];
    return raw.map((id) => parseInt(id)).filter((id) => !Number.isNaN(id));
  };

  const getUserGroupNames = (user) => {
    const groupIds = parseGroupIds(user.group_ids);
    if (groupIds.length === 0) return [];
    return groupIds
      .map((id) => groups.find((group) => parseInt(group.id) === id)?.name)
      .filter(Boolean);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterChange = (event) => {
    setFilterStatus(event.target.value);
  };

  // Validate form data
  const validateForm = () => {
    const errors = {};
    
    if (!formData.first_name.trim()) {
      errors.first_name = 'First name is required';
    }
    
    if (!formData.last_name.trim()) {
      errors.last_name = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!editingUser && !formData.password.trim()) {
      errors.password = 'Password is required';
    } else if (!editingUser && formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleShowEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      password: '',
      role: 1,
      status: user.status,
      group_ids: parseGroupIds(user.group_ids).map((id) => id.toString())
    });
    setFormErrors({});
    setShowModal(true);
  };

  const fetchLicenseInfo = async () => {
    if (!orgId) return;
    try {
      const response = await api.licenses.getOrganizationUsage(orgId);
      setLicenseInfo(response.data?.data || null);
    } catch (error) {
      console.error('Error fetching license info:', error);
    }
  };

  const handleShowAddModal = () => {
    setEditingUser(null);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      role: 1,
      status: 1,
      group_ids: []
    });
    setFormErrors({});
    fetchLicenseInfo();
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      role: 1,
      status: 1,
      group_ids: []
    });
    setFormErrors({});
    setShowPassword(false);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    
    if (name === 'status') {
      setFormData(prev => ({ ...prev, [name]: parseInt(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleUpdateUser = async (event) => {
    event.preventDefault();
    
    if (!editingUser) return;
    
    setModalLoading(true);
    
    try {
      await api.organizations.updateUser(orgId, editingUser.id, formData);
      toast.success('User updated successfully!');
      handleCloseModal();
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user: ' + (error.response?.data?.message || error.message));
    } finally {
      setModalLoading(false);
    }
  };

  const handleSaveUser = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    if (!editingUser) {
      setModalLoading(true);
      try {
        const licenseCheck = await api.licenses.checkAvailability(orgId);
        if (!licenseCheck.data.success || !licenseCheck.data.data.available) {
          const message = licenseCheck.data.data.message || 'No licenses available for new users. Please request more licenses from the organization admin.';
          toast.error(message);
          setModalLoading(false);
          return;
        }
      } catch (error) {
        console.error('Error checking license availability:', error);
        toast.error('Failed to check license availability. Please try again.');
        setModalLoading(false);
        return;
      }
      setModalLoading(false);
    }
    
    setModalLoading(true);
    
    try {
      const userData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        role: 1,
        status: parseInt(formData.status),
        group_ids: formData.group_ids.map((id) => parseInt(id)).filter((id) => !Number.isNaN(id)),
        ...(formData.password && { password: formData.password })
      };

      if (editingUser) {
        await api.organizations.updateUser(orgId, editingUser.id, userData);
        toast.success('User updated successfully!');
      } else {
        await api.organizations.addUser(orgId, userData);
        
        try {
          await api.licenses.consumeLicense(orgId);
          toast.success('User created successfully! License consumed.');
        } catch (licenseError) {
          console.warn('User created but failed to consume license:', licenseError);
          toast.warning('User created successfully, but failed to update license count. Please refresh the license panel.');
        }
      }
      
      handleCloseModal();
      fetchUsers();
      if (!editingUser) {
        fetchLicenseInfo();
      }
    } catch (error) {
      console.error('Error saving user:', error);
      console.error('Error response:', error.response?.data);
      
      // Get error message
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message;
      const errorData = error.response?.data;
      
      // Check for duplicate email error - be specific
      if (errorMessage?.toLowerCase().includes('user with this email already exists') ||
          errorMessage?.toLowerCase().includes('email already exists') ||
          (errorMessage?.toLowerCase().includes('duplicate') && (errorMessage?.toLowerCase().includes('email') || errorData?.error?.toLowerCase().includes('email'))) ||
          (errorData?.error && errorData.error.toLowerCase().includes('email') && errorData.error.toLowerCase().includes('unique'))) {
        toast.error('User account has already registered with this email. Please use a different email address.');
      } else if (errorMessage?.toLowerCase().includes('validation error')) {
        // Show validation errors more clearly
        const validationError = errorData?.error || errorMessage;
        toast.error('Validation error: ' + validationError);
      } else {
        // Show generic error
        toast.error('Failed to save user: ' + errorMessage);
      }
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    
    setDeleteLoading(userToDelete.id);
    
    try {
      await api.organizations.removeUser(orgId, userToDelete.id);
      
      try {
        await api.licenses.releaseLicense(orgId);
        toast.success('User removed from organization successfully! License released.');
      } catch (licenseError) {
        console.warn('User deleted but failed to release license:', licenseError);
        toast.warning('User removed successfully, but failed to update license count. Please refresh the license panel.');
      }
      setShowDeleteModal(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      console.error('Error removing user from organization:', error);
      toast.error('Failed to remove user: ' + (error.response?.data?.message || error.message));
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const handleRestoreUser = async (user) => {
    try {
      await api.organizations.restoreUser(orgId, user.id);
      toast.success(`User "${user.first_name} ${user.last_name}" has been restored`);
      fetchUsers();
    } catch (error) {
      console.error('Error restoring user:', error);
      toast.error('Failed to restore user: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleToggle2fa = async (targetUser) => {
    try {
      if (!targetUser?.id) return;

      const nextEnabled = !Boolean(targetUser.is_2fa_enabled);
      const confirmMsg = nextEnabled
        ? `Enable 2FA for ${getUserDisplayName(targetUser)}? They will be forced to set up authenticator on next login.`
        : `Disable 2FA for ${getUserDisplayName(targetUser)}? This will remove existing 2FA configuration.`;

      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(confirmMsg);
      if (!confirmed) return;

      setTwofaLoadingUserId(targetUser.id);
      await api.users.set2faEnabled(targetUser.id, nextEnabled);
      toast.success(nextEnabled ? '2FA enabled (setup required on next login)' : '2FA disabled');
      await fetchUsers();
    } catch (error) {
      console.error('Error toggling 2FA:', error);
      toast.error(error.response?.data?.message || 'Failed to update 2FA setting');
    } finally {
      setTwofaLoadingUserId(null);
    }
  };

  const handleOpenAssignModal = () => {
    setAssignGroupId('');
    setAssignAssigned([]);
    setAssignAvailable([]);
    if (groups.length === 0) {
      fetchGroups();
    }
    setShowAssignModal(true);
  };

  const handleCloseAssignModal = () => {
    setShowAssignModal(false);
    setAssignGroupId('');
    setAssignAssigned([]);
    setAssignAvailable([]);
  };

  const handleAssignGroupChange = async (event) => {
    const groupId = event.target.value;
    setAssignGroupId(groupId);
    setAssignAssigned([]);
    setAssignAvailable([]);

    if (!groupId) return;

    try {
      setAssignLoading(true);
      const response = await api.groups.getUsers(groupId, orgId);
      if (!response.data?.success) {
        toast.error(response.data?.message || 'Failed to load learners for this group.');
        return;
      }
      const { assigned, available } = response.data.data;
      setAssignAssigned(Array.isArray(assigned) ? assigned : []);
      setAssignAvailable(Array.isArray(available) ? available : []);
    } catch (error) {
      toast.error('Failed to load learners: ' + (error.response?.data?.message || error.message));
    } finally {
      setAssignLoading(false);
    }
  };

  const handleSaveAssignments = async () => {
    if (!assignGroupId) {
      toast.error('Please select a group first.');
      return;
    }

    try {
      setAssignSaving(true);
      await api.groups.setUsers(assignGroupId, assignAssigned.map((user) => user.id), orgId);
      toast.success('Group assignments saved successfully.');
      await fetchUsers();
      handleCloseAssignModal();
    } catch (error) {
      toast.error('Failed to save assignments: ' + (error.response?.data?.message || error.message));
    } finally {
      setAssignSaving(false);
    }
  };

  const handleGoBack = () => {
    navigate('/admin/organizations');
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    let matchesStatus = true;
    switch(filterStatus) {
      case 'active':
        matchesStatus = user.status === 1 && !user.deleted;
        break;
      case 'inactive':
        matchesStatus = user.status === 0 && !user.deleted;
        break;
      case 'deleted':
        matchesStatus = user.deleted;
        break;
      default:
        matchesStatus = !user.deleted;
        break;
    }
    
    return matchesSearch && matchesStatus;
  });

  const getUserInitials = (user) => {
    return ((user.first_name?.[0] || '') + (user.last_name?.[0] || '')).toUpperCase() || 'U';
  };

  const getUserDisplayName = (user) => {
    return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96 p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={handleGoBack}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-purple-300 text-purple-700 rounded-lg shadow-md hover:bg-purple-50 hover:shadow-lg transition-all duration-200 text-sm font-medium"
          >
            <FaArrowLeft className="w-4 h-4" /> Back
          </button>
          <h5 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
            {organizationName}'s Org Learners
          </h5>
        </div>
        <Button
          bsPrefix="custom-hamburger"
          onClick={handleOpenAssignModal}
          className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm flex items-center justify-center gap-2 px-4 py-2"
        >
          <FaUsersCog className="w-4 h-4" />
          <span className="whitespace-nowrap">Assign Group</span>
        </Button>
      </div>

      {/* Search + Filter + Add */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
        <div className="lg:col-span-2">
          <div className="flex shadow-sm rounded-lg sm:rounded-xl overflow-hidden bg-white">
            <div className="flex items-center px-3 bg-white border-r border-gray-200">
              <FaSearch className="w-4 h-4 text-purple-600" />
            </div>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={handleSearch}
              className="w-full px-3 py-2 border-0 focus:ring-2 focus:ring-purple-500 text-sm sm:text-base outline-none"
            />
          </div>
        </div>
        <select
          value={filterStatus}
          onChange={handleFilterChange}
          className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-sm form-select"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="deleted">Deleted</option>
        </select>
        <button
          onClick={handleShowAddModal}
          className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2  custom-hamburger custom-hamburger-primary p-2"
        >
          <FaPlus className="w-4 h-4" />
          <span className="whitespace-nowrap">Add Learner</span>
        </button>
        <button
          onClick={() => setShowBulkUploadModal(true)}
          className="w-full border-1 border-purple-600 bg-white text-purple-600 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2 custom-hamburger"
        >
          <FaUpload className="w-4 h-4" />
          <span className="whitespace-nowrap">Bulk Upload Users</span>
        </button>
      </div>

      {/* No Data */}
      {filteredUsers.length === 0 ? (
        <div className="text-center py-8 rounded-xl sm:rounded-2xl bg-white/80 backdrop-blur shadow-md border border-purple-100">
          <p className="text-sm sm:text-base font-medium text-purple-700">
            {searchTerm || filterStatus !== 'all' 
              ? 'No learners found matching your criteria.' 
              : 'No learners in this organization yet. Click "Add Learner" to get started.'
            }
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-3 sm:mx-0 rounded-xl sm:rounded-2xl shadow-lg bg-white">
          <table className="m-0 min-w-max text-xs sm:text-sm table table-bordered table-hover">
            <thead className="bg-gradient-to-r text-gray-800 to-pink-600 text-white">
              <tr>
                <th className="py-2 px-3 text-left font-bold">Name</th>
                <th className="py-2 px-3 text-left font-bold">Email</th>
                <th className="py-2 px-3 text-left font-bold">Group</th>
                <th className="py-2 px-3 text-left font-bold">Status</th>
                <th className="py-2 px-3 text-left font-bold">2FA Enabled</th>
                <th className="py-2 px-3 text-left font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id} className={`border-b border-purple-100 hover:bg-purple-50/50 transition-all ${user.deleted ? 'bg-red-50' : ''}`}>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-3">
                 
                      <div>
                        <span className="text-gray-800">{getUserDisplayName(user)}</span>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 px-3 text-gray-800 break-all">{user.email}</td>
                  <td className="py-2 px-3 text-left">
                    {(() => {
                      const groupNames = getUserGroupNames(user);
                      if (groupNames.length === 0) {
                        return <span className="text-gray-400 text-xs">-</span>;
                      }
                      return (
                        <div className="d-flex flex-wrap gap-1">
                          {groupNames.map((name) => (
                            <Badge key={name} bg="info" className="px-2 py-1 text-xs">{name}</Badge>
                          ))}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="py-2 px-3 text-left">
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${user.status === 1 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                      {user.status === 1 ? <FaCheck /> : <FaTimes />}
                      {user.status === 1 ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-left">
                    <div className="d-flex align-items-center gap-2">
                      <Form.Check
                        type="switch"
                        id={`twofa-${user.id}`}
                        label={Boolean(user.is_2fa_enabled) ? 'On' : 'Off'}
                        checked={Boolean(user.is_2fa_enabled)}
                        disabled={twofaLoadingUserId === user.id || Boolean(user.deleted)}
                        onChange={() => handleToggle2fa(user)}
                      />
                      {twofaLoadingUserId === user.id ? (
                        <Spinner animation="border" size="sm" />
                      ) : null}
                    </div>
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex gap-2 justify-left">
                      {!user.deleted ? (
                        <>
                          <button
                          bsPrefix="custom-hamburger"
                            onClick={() => handleShowEditModal(user)}
                            className="border-0 border-purple-300 text-purple-600 rounded hover:bg-purple-50 transition p-1"
                            title="Edit User"
                          >
                            <FaEdit className="w-5 h-5" />
                          </button>
                          <button
                          bsPrefix="custom-hamburger"
                            onClick={() => handleDeleteClick(user)}
                            disabled={deleteLoading === user.id}
                            className="border-0 border-red-300 text-red-600 hover:bg-red-50 rounded transition disabled:opacity-50 p-1"
                            title="Remove from Organization"
                          >
                            {deleteLoading === user.id ? <div className="w-5 h-5 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div> : <FaTrash className="w-5 h-5" />}
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleRestoreUser(user)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition"
                          title="Restore User"
                        >
                          <FaCheck className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            {editingUser ? 'Edit User' : 'Add New User'}
          </Modal.Title>
        </Modal.Header>

        {!editingUser && licenseInfo && (
          <div className="bg-purple-50 border-b border-purple-200 p-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <small className="text-purple-600">Total Licenses</small>
                <div className="text-xl font-bold text-purple-700">{licenseInfo.total_licenses}</div>
              </div>
              <div>
                <small className="text-purple-600">Used</small>
                <div className="text-xl font-bold text-orange-600">{licenseInfo.used_licenses}</div>
              </div>
              <div>
                <small className="text-purple-600">Available</small>
                <div className={`text-xl font-bold ${licenseInfo.remaining_licenses > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {licenseInfo.remaining_licenses}
                </div>
              </div>
            </div>
            {licenseInfo.remaining_licenses === 0 && (
              <div className="mt-3 p-3 bg-red-100 text-red-700 rounded-lg text-sm text-center">
                No licenses remaining. Contact admin to request more.
              </div>
            )}
          </div>
        )}

        <Modal.Body className="bg-gray-50 p-3 sm:p-5">
          <Form onSubmit={handleSaveUser}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">First Name <span className="text-red-500">*</span></label>
                  <input
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 rounded-lg border ${formErrors.first_name ? 'border-red-500' : 'border-purple-200'} focus:ring-2 focus:ring-purple-500 outline-none text-sm`}
                    placeholder="Enter first name"
                  />
                  {formErrors.first_name && <p className="text-red-500 text-xs mt-1">{formErrors.first_name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Last Name <span className="text-red-500">*</span></label>
                  <input
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 rounded-lg border ${formErrors.last_name ? 'border-red-500' : 'border-purple-200'} focus:ring-2 focus:ring-purple-500 outline-none text-sm`}
                    placeholder="Enter last name"
                  />
                  {formErrors.last_name && <p className="text-red-500 text-xs mt-1">{formErrors.last_name}</p>}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                <input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={editingUser}
                  readOnly={editingUser}
                  className={`w-full px-3 py-2 rounded-lg border ${formErrors.email ? 'border-red-500' : 'border-purple-200'} focus:ring-2 focus:ring-purple-500 outline-none text-sm ${editingUser ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  placeholder="Enter email address"
                />
                {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
                {editingUser && <p className="text-xs text-gray-500 mt-1">Email cannot be changed after creation</p>}
              </div>

              <div className="mb-4 relative">
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Password {!editingUser && <span className="text-red-500">*</span>}
                  {editingUser && <span className="text-gray-500 text-xs"> (leave blank to keep current)</span>}
                </label>
                <div className="relative">
                  <input
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 rounded-lg border ${formErrors.password ? 'border-red-500' : 'border-purple-200'} focus:ring-2 focus:ring-purple-500 outline-none text-sm pr-10`}
                    placeholder={editingUser ? "New password (optional)" : "Enter password"}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-purple-600"
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                {formErrors.password && <p className="text-red-500 text-xs mt-1">{formErrors.password}</p>}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Organization Groups <span className="text-gray-500 text-xs font-normal">(optional)</span>
                </label>
                <div className="border border-purple-200 rounded-lg p-3 bg-white max-h-48 overflow-y-auto">
                  {orgGroups.length === 0 ? (
                    <p className="text-muted text-sm mb-0">No groups linked to this organization.</p>
                  ) : (
                    orgGroups.map((group) => {
                      const groupId = group.id.toString();
                      return (
                        <Form.Check
                          key={group.id}
                          type="checkbox"
                          id={`org-learner-group-${groupId}`}
                          label={group.name}
                          checked={formData.group_ids.includes(groupId)}
                          onChange={() => handleGroupToggle(groupId)}
                          className="mb-2"
                        />
                      );
                    })
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">Only organization groups are listed.</p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-purple-500 outline-none text-sm"
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>

            <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
              <Row className="w-100 g-3">
                <Col md={6}>
                  <Button bsPrefix="custom-hamburger" onClick={handleCloseModal} disabled={modalLoading} className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                    Cancel
                  </Button>
                </Col>
                <Col md={6}>
                  <Button bsPrefix="custom-hamburger" type="submit" disabled={modalLoading} className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                    {modalLoading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        {editingUser ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      editingUser ? 'Update User' : 'Create User'
                    )}
                  </Button>
                </Col>
              </Row>
            </Modal.Footer>
          </Form>
        </Modal.Body>
      </Modal>

      <OrgBulkUploadUsersModal
        show={showBulkUploadModal}
        onHide={() => setShowBulkUploadModal(false)}
        orgId={orgId}
        onImported={async () => {
          await fetchUsers();
          await fetchLicenseInfo();
        }}
      />

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-10 bg-gray-50">
          <div className="mb-4">
            <FaTrash size={60} className="text-red-500 mx-auto" />
          </div>
          <h5 className="font-bold text-gray-800 mb-3">Remove User</h5>
          <p className="text-gray-600">
            Are you sure you want to remove <strong>{userToDelete?.first_name} {userToDelete?.last_name}</strong> from this organization?
          </p>
          <p className="text-sm text-gray-500">This is a soft delete. The user can be restored later.</p>
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Row className="w-100 g-3">
            <Col md={6}>
              <Button bsPrefix="custom-hamburger" onClick={handleDeleteCancel} className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                Cancel
              </Button>
            </Col>
            <Col md={6}>
              <Button bsPrefix="custom-hamburger" onClick={handleDeleteConfirm} disabled={deleteLoading} className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                {deleteLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Removing...
                  </>
                ) : (
                  'Remove User'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>

      {/* Assign Group Modal */}
      <Modal show={showAssignModal} onHide={handleCloseAssignModal} size="xl" centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            Assign Group to Learners
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-gray-50 p-3 sm:p-5">
          <Form.Group className="mb-4 max-w-md">
            <Form.Label className="font-semibold text-gray-700 text-sm">
              Select Group <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={assignGroupId}
              onChange={handleAssignGroupChange}
              disabled={assignLoading || assignSaving}
              className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
            >
              <option value="">Choose a group...</option>
              {groups
                .filter((group) => parseInt(group.organization_id) === parseInt(orgId))
                .map((group) => (
                  <option key={group.id} value={group.id}>{group.name}</option>
                ))}
            </Form.Select>
          </Form.Group>

          {assignLoading ? (
            <div className="flex justify-center items-center min-h-64">
              <Spinner animation="border" className="text-purple-600" />
            </div>
          ) : assignGroupId ? (
            <GroupAssignBoard
              assigned={assignAssigned}
              available={assignAvailable}
              setAssigned={setAssignAssigned}
              setAvailable={setAssignAvailable}
            />
          ) : (
            <div className="text-center py-10 text-sm text-gray-500">
              Select a group above to assign or deassign learners.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Row className="w-100 g-3">
            <Col md={6}>
              <Button bsPrefix="custom-hamburger" onClick={handleCloseAssignModal} disabled={assignSaving} className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                Cancel
              </Button>
            </Col>
            <Col md={6}>
              <Button bsPrefix="custom-hamburger" onClick={handleSaveAssignments} disabled={assignSaving || !assignGroupId} className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                {assignSaving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FaSave className="w-4 h-4" /> Save Assignments
                  </>
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OrgUsers;